import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCopyToClipboardResult {
  copy: (value: string) => Promise<boolean>;
  copied: boolean;
  error: Error | null;
}

/**
 * `copied` flips back on its own after `resetAfter`, so a button can show a
 * check without the caller running a timer.
 *
 * Falls back to a hidden textarea because `navigator.clipboard` is unavailable
 * outside secure contexts.
 */
export function useCopyToClipboard(resetAfter = 1600): UseCopyToClipboardResult {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (value: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const field = document.createElement('textarea');
          field.value = value;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(field);
          if (!ok) throw new Error('No se pudo copiar');
        }
        setError(null);
        setCopied(true);
        if (timer.current !== undefined) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), resetAfter);
        return true;
      } catch (caught) {
        setCopied(false);
        setError(caught instanceof Error ? caught : new Error('No se pudo copiar'));
        return false;
      }
    },
    [resetAfter],
  );

  return { copy, copied, error };
}
